import {
  A3Stadiumlight,
  A4PowerGrid,
  B1Battery,
  B1Electricityconsumptionofhome,
  B3Photovoltaic,
  CZHome,
  DynamicPriceIcon,
  I1ElectricalChargingStation,
  I1Electriccar,
  J2Lock,
} from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useTheme } from '@mui/system'
import { isUndefined } from 'lodash'
import React, { useContext, useMemo } from 'react'
import { useSocketConnectionContext } from 'src/Elements/Framework/SystemDataProvider'
import { liveDataValueFormatter } from 'src/Elements/LiveView/LiveView'
import PopoverItems, { PopoverItem } from 'src/Elements/LiveView/PopoverItems'
import { LiveItemType } from 'src/Elements/LiveView/types'
import { LiveViewItemBase } from 'src/Libs/live-view'
import { getPowermeterLiveItemLabel } from 'src/Libs/live-view/utils/labels'
import { useWallboxes } from 'src/Libs/wallboxes'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { LiveDataModel, PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import { cleanText } from 'src/Libs/utils'

interface Props {
  batteryHomePower: number
  homeBatteryPower: number
  pvHomePower: number
  networkHomePower: number
  homeNetworkPower: number
  consumption: number
  homeConsumerPower: number
  consumers?: PowermeterHardwareModel[]
  producers?: PowermeterHardwareModel[]
  wallboxes?: WallboxHardwareModel[]
  liveData?: LiveDataModel
  offlineClientStorageIds?: string[]
  offlineIcon?: React.ReactNode
  homeWallboxPower: number
  trackerHomePower: number
  shallowLiveView?: boolean
  isBlockingTimeActive?: boolean
  isDynamicPowerPriceEnabled?: boolean
  showDynamicPrices?: boolean
  storageId?: string
}

/**
 * Hook that returns the live view base items with the shared values of the live views.
 */
export const useBaseLiveValueItems = ({
  batteryHomePower,
  homeBatteryPower,
  pvHomePower,
  networkHomePower,
  homeNetworkPower,
  consumption,
  homeConsumerPower,
  homeWallboxPower,
  consumers,
  liveData,
  offlineClientStorageIds,
  offlineIcon,
  producers,
  trackerHomePower,
  wallboxes,
  isBlockingTimeActive,
  isDynamicPowerPriceEnabled,
  showDynamicPrices,
  storageId,
}: Props): {
  batteryLiveItem: LiveViewItemBase
  pvHomePowerLiveItem: LiveViewItemBase
  evsesSubLiveItem?: LiveViewItemBase
  homeConsumptionLiveItem: LiveViewItemBase
  externalConsumerSubLiveItem?: LiveViewItemBase
  consumptionPowerLiveItem: LiveViewItemBase
  externalProduction: LiveViewItemBase
  networkHomePowerLiveItem: LiveViewItemBase
} => {
  const { t } = useCustomTranslation()
  const wallboxCount = wallboxes?.length ?? 0
  const consumerCount = consumers?.length ?? 0
  const producerCount = producers?.length ?? 0
  const { wallboxes: configurationWallboxes } = useWallboxes()
  const { wallboxSocketState, wallboxChargingSocketState } = useContext(useSocketConnectionContext())
  const theme = useTheme()

  const batteryLiveItem: LiveViewItemBase = useMemo(
    () => ({
      icon: <B1Battery sx={{ height: '100%', width: '100%', padding: '9px' }} />,
      label: cleanText(t('liveView.battery')),
      value: liveDataValueFormatter((batteryHomePower ?? 0) > 0 ? batteryHomePower : (homeBatteryPower ?? 0) > 0 ? homeBatteryPower : 0),
      doNotFlow: !batteryHomePower && !homeBatteryPower,
      directionIsReversed: (batteryHomePower ?? 0) > 0,
      type: LiveItemType.battery,
      overlayIcon: isBlockingTimeActive && (
        <J2Lock
          sx={{
            '& path': {
              fill: theme => theme.palette.primary.dark,
            },
          }}
        />
      ),
    }),
    [batteryHomePower, homeBatteryPower, isBlockingTimeActive, t]
  )

  const pvHomePowerLiveItem: LiveViewItemBase = useMemo(
    () => ({
      icon: <B3Photovoltaic sx={{ height: '100%', width: '100%', padding: '9px' }} />,
      label: cleanText(t('liveView.pv')),
      value: liveDataValueFormatter(pvHomePower),
      doNotFlow: !pvHomePower,
      directionIsReversed: true,
      type: LiveItemType.pvHomePower,
    }),
    [pvHomePower, t]
  )

  const networkHomePowerLiveItem: LiveViewItemBase = useMemo(
    () => ({
      icon: <A4PowerGrid sx={{ height: '100%', width: '100%', padding: '9px' }} />,
      label: cleanText(t('liveView.grid')),
      value: liveDataValueFormatter((networkHomePower ?? 0) > 0 ? networkHomePower : (homeNetworkPower ?? 0) > 0 ? homeNetworkPower : 0),
      doNotFlow: !networkHomePower && !homeNetworkPower,
      directionIsReversed: (networkHomePower ?? 0) > 0,
      type: LiveItemType.networkHomePower,
      overlayIcon: isDynamicPowerPriceEnabled ? <DynamicPriceIcon color="primary" /> : undefined,
    }),
    [homeNetworkPower, networkHomePower, isDynamicPowerPriceEnabled, t]
  )

  const wallboxItems = useMemo(
    () =>
      wallboxes?.map(wallbox => {
        const matchingConfigurationWallbox = configurationWallboxes.find(
          wb => wb.wallboxIndex === wallbox.number && wb.storageSerialNo === wallbox.storageSerialNumber.toString()
        )
        const isWallboxStorageOffline =
          Boolean(offlineClientStorageIds?.includes(wallbox.storageSerialNumber.toString())) || !matchingConfigurationWallbox?.isAvailable
        const lastEvent = wallboxSocketState.data?.find(event => event.wallboxId === wallbox.wallboxId)
        const matchingChargingState = wallboxChargingSocketState.data?.find(event => event.wallboxId === wallbox?.wallboxId)
        const value = (lastEvent?.l1 ?? 0) + (lastEvent?.l2 ?? 0) + (lastEvent?.l3 ?? 0)
        const baseIcon = matchingChargingState?.energyAll ? (
          <I1Electriccar color={theme.palette.grey.grey03} />
        ) : (
          <I1ElectricalChargingStation color={theme.palette.grey.grey03} />
        )
        const icon = isWallboxStorageOffline ? offlineIcon : baseIcon

        return {
          id: wallbox.id?.toString(),
          label: wallbox.deviceName ?? cleanText(t('dashboard.liveView.wallbox')),
          value: isWallboxStorageOffline ? null : liveDataValueFormatter(value),
          isOffline: isWallboxStorageOffline,
          Icon: icon,
        } as PopoverItem
      }),
    [wallboxes, t, wallboxSocketState.data, offlineClientStorageIds, offlineIcon, wallboxChargingSocketState, configurationWallboxes, theme]
  )

  const wallboxPopoverProps = useMemo(
    () =>
      wallboxCount > 1
        ? {
            badgeAmount: wallboxCount,
            popoverContent: <PopoverItems items={wallboxItems || []} />,
          }
        : {},
    [wallboxCount, wallboxItems]
  )

  const evsesSubLiveItem: LiveViewItemBase | undefined = useMemo(
    () =>
      wallboxCount
        ? {
            icon: <I1ElectricalChargingStation sx={{ height: '100%', width: '100%', padding: '12px' }} />,
            label: wallboxCount > 1 ? cleanText(t('liveView.wallboxes')) : wallboxes?.[0]?.deviceName ?? cleanText(t('dashboard.liveView.wallbox')),
            // Direction (charging/discharging) is shown by the arrow, so the value is displayed without a sign
            value: liveDataValueFormatter(Math.abs(homeWallboxPower)),
            doNotFlow: !homeWallboxPower,
            directionIsReversed: homeWallboxPower < 0,
            type: LiveItemType.evsesSub,
            overlayIcon: showDynamicPrices ? <DynamicPriceIcon color="primary" /> : undefined,
            ...wallboxPopoverProps,
          }
        : undefined,
    [wallboxCount, t, wallboxes, homeWallboxPower, showDynamicPrices, wallboxPopoverProps]
  )

  const homeConsumption = useMemo<number>(() => (consumption ?? 0) - (homeConsumerPower ?? 0), [homeConsumerPower, consumption])

  const homeConsumptionLiveItem: LiveViewItemBase = useMemo(
    () => ({
      icon: <CZHome sx={{ height: '100%', width: '100%', padding: '9px' }} />,
      label: cleanText(t('liveView.homeConsumer')),
      value: liveDataValueFormatter(homeConsumption),
      doNotFlow: !homeConsumption,
      directionIsReversed: false,
      type: LiveItemType.homeConsumption,
    }),
    [t, homeConsumption]
  )

  const externalConsumerPopoverProps = useMemo(
    () =>
      consumerCount > 1
        ? {
            badgeAmount: consumerCount,
            popoverContent: (
              <PopoverItems
                items={
                  consumers?.map(consumer => {
                    const isConsumerStorageOffline = Boolean(offlineClientStorageIds?.includes(consumer?.serialno?.toString() ?? ''))
                    const foundLiveItem = liveData?.powermeters?.consumers.find(
                      liveConsumer => liveConsumer?.deviceID?.[1] === consumer?.deviceID?.[1]
                    )
                    const foundLiveItemPower = foundLiveItem?.power
                    const label = consumer?.name?.[1] || cleanText(t('dashboard.liveView.consumer'))
                    const consumersPositiveValue = (foundLiveItemPower ?? 0) > 0 ? foundLiveItemPower : 0
                    const icon =
                      isConsumerStorageOffline || isUndefined(foundLiveItem?.power) ? (
                        offlineIcon
                      ) : (
                        <B1Electricityconsumptionofhome color={theme.palette.grey.grey03} />
                      )

                    return {
                      id: consumer.serialno?.toString(),
                      label,
                      value: isConsumerStorageOffline ? null : liveDataValueFormatter(consumersPositiveValue),
                      Icon: icon,
                    } as PopoverItem
                  }) || []
                }
              />
            ),
          }
        : {},
    [consumerCount, consumers, liveData?.powermeters?.consumers, offlineClientStorageIds, t, offlineIcon, theme]
  )
  const {
    showCustomLabel: showConsumerCustomLabel,
    customLabel: consumerCustomLabel,
    fallbackTranslationKey: consumerFallbackTranslationKey,
  } = getPowermeterLiveItemLabel(consumers, false)
  const externalConsumerSubLiveItem: LiveViewItemBase | undefined = useMemo(
    () =>
      consumerCount
        ? {
            icon: <A3Stadiumlight sx={{ height: '100%', width: '100%', padding: '9px' }} />,
            label: showConsumerCustomLabel ? consumerCustomLabel : cleanText(t(consumerFallbackTranslationKey)),
            value: liveDataValueFormatter(homeConsumerPower),
            doNotFlow: !homeConsumerPower,
            directionIsReversed: false,
            type: LiveItemType.externalConsumerSub,
            ...externalConsumerPopoverProps,
          }
        : undefined,
    [consumerCount, homeConsumerPower, t, externalConsumerPopoverProps, showConsumerCustomLabel, consumerCustomLabel, consumerFallbackTranslationKey]
  )

  const consumptionPower = useMemo(() => (consumption ?? 0) + (homeWallboxPower ?? 0), [consumption, homeWallboxPower])

  const consumptionPowerLiveItem: LiveViewItemBase = {
    icon: <B1Electricityconsumptionofhome sx={{ width: '100%', height: '100%', padding: '12px' }} />,
    label: cleanText(t('liveView.consumption')),
    value: liveDataValueFormatter(consumptionPower),
    doNotFlow: !consumptionPower,
    directionIsReversed: consumptionPower < 0,
    type: LiveItemType.consumptionPower,
  }

  const hasMultipleProducers = producerCount > 1

  const externalProductionPopoverProps = useMemo(
    () =>
      producerCount > 1
        ? {
            badgeAmount: producerCount,
            popoverContent: (
              <PopoverItems
                items={
                  producers?.map(producer => {
                    const isProducerStorageOffline = Boolean(offlineClientStorageIds?.includes(producer?.serialno?.toString() ?? ''))
                    const foundLiveItem = liveData?.powermeters?.producers.find(
                      liveProducer => liveProducer?.deviceID?.[1] === producer?.deviceID?.[1]
                    )
                    const foundLiveItemPower = foundLiveItem?.power
                    const label = producer?.name?.[1] || cleanText(t('dashboard.liveView.producer'))
                    const producersPositiveValue = (foundLiveItemPower ?? 0) < 0 ? foundLiveItemPower : 0
                    const icon =
                      isProducerStorageOffline || isUndefined(foundLiveItem?.power) ? (
                        offlineIcon
                      ) : (
                        <B1Electricityconsumptionofhome color={theme.palette.grey.grey03} />
                      )

                    return {
                      id: producer.serialno?.toString(),
                      label,
                      value: isProducerStorageOffline ? null : liveDataValueFormatter(producersPositiveValue),
                      Icon: icon,
                    } as PopoverItem
                  }) || []
                }
              />
            ),
          }
        : {},
    [producerCount, producers, liveData?.powermeters?.producers, offlineClientStorageIds, t, offlineIcon, theme]
  )

  const { showCustomLabel, customLabel, fallbackTranslationKey } = getPowermeterLiveItemLabel(producers, true)

  const externalProduction: LiveViewItemBase = useMemo(
    () => ({
      doNotFlow: !trackerHomePower,
      value: liveDataValueFormatter(trackerHomePower),
      label: showCustomLabel ? customLabel : cleanText(t(fallbackTranslationKey)),
      icon: <A3Stadiumlight sx={{ height: '100%', width: '100%', padding: '9px' }} />,
      directionIsReversed: true,
      type: LiveItemType.externalProduction,
      ...externalProductionPopoverProps,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackerHomePower, producers, hasMultipleProducers]
  )

  return {
    batteryLiveItem,
    pvHomePowerLiveItem,
    evsesSubLiveItem,
    homeConsumptionLiveItem,
    externalConsumerSubLiveItem,
    consumptionPowerLiveItem,
    externalProduction,
    networkHomePowerLiveItem,
  }
}
